import { Injectable } from '@angular/core';
import { FOYData } from './foy-data';
import { FoyService } from '../api/services';
import { LanguageService } from './language.service';
import { Subject } from 'rxjs';
import { tap, filter, switchMap } from 'rxjs/operators';
import { AccountService } from './account.service';
import { FoyData, InformationDialogComponent, SurveyType } from '../public-api';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class FOYDataService {
  private currencyQuestionIds = [];
  private industryQuestionIds = [113101, 182233, 182366, 182499, 182675];
  private currencyTag = '$currency$';
  private currencySignsMapping: { [id: string]: string } = {
    EUR: '€',
    USD: '$',
  };

  public data: FOYData;
  public $currencyHasChanged = new Subject<string>();

  public surveyTypeHasChanged = new Subject<number>();

  constructor(
    private foyService: FoyService,
    private languageService: LanguageService,
    private accountService: AccountService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    this.init();
    this.foyService.apiFoyGetCurrencyQuestionsGet$Json().subscribe((currencyQuestionIds) => {
      this.currencyQuestionIds = currencyQuestionIds;
    });

    this.foyService.apiFoyGetIndustryQuestionsGet$Json().subscribe((industryQuestionIds) => {
      this.industryQuestionIds = industryQuestionIds;
    });
  }

  init() {
    this.data = {
      currentCurrency: 'EUR',
      surveyType: null,
    };
    this.setupCurrencySubscription();
  }

  setupCurrencySubscription() {
    this.accountService.selectedSurveyInstanceId$
      .pipe(
        filter((selectedSurveyInstanceId) => selectedSurveyInstanceId != null),
        switchMap((selectedSurveyInstanceId) => {
          const user = this.accountService.getUser();
          const selectedSurvey = user.surveys.find((s) => s.surveyInstanceId === selectedSurveyInstanceId);

          if (!selectedSurvey || selectedSurvey.surveyId == null) {
            return [];
          }

          return this.foyService.apiFoyGet$Json({
            language: this.languageService.language,
            surveyInstanceId: selectedSurveyInstanceId,
            surveyId: selectedSurvey.surveyId,
          });
        }),
      )
      .subscribe((data) => {
        this.handleFoyResponse(data);
      });
  }

  updateCurrency() {
    return this.foyService
      .apiFoyGet$Json({
        language: this.languageService.language,
        surveyInstanceId: this.accountService.selectedSurveyInstanceId$.value,
        surveyId: this.accountService
          .getUser()
          .surveys.find((s) => s.surveyInstanceId === this.accountService.selectedSurveyInstanceId$.value)?.surveyId,
      })
      .pipe(
        tap((data) => {
          this.handleFoyResponse(data);
        }),
      );
  }

  updateCurrencyForUser(userId: number, selectedSurveyInstanceId: number, selectedSurveyId: number) {
    return this.foyService
      .apiFoyGetForUserGet$Json({
        language: this.languageService.language,
        userId: userId,
        surveyInstanceId: selectedSurveyInstanceId,
        surveyId: selectedSurveyId,
      })
      .pipe(
        tap((data) => {
          this.handleFoyResponse(data);
        }),
      );
  }

  isCurrencyQuestion(questionId: number) {
    return this.currencyQuestionIds.includes(questionId);
  }

  isIndustryQuestion(questionId: number) {
    return this.industryQuestionIds.includes(questionId);
  }

  getNumberOfDecimalPlaces(hint: string, defaultNumberOfDigits: number) {
    if (hint == null) {
      return defaultNumberOfDigits;
    }
    if (this.isCurrencyThousandText(hint)) {
      return 0;
    }
    if (this.isFTEText(hint)) {
      return 1;
    }
    if (this.isHoursText(hint)) {
      return 1;
    }

    return defaultNumberOfDigits;
  }

  isCurrencyThousandText(text: string) {
    return text.includes(`k ${this.currencyTag}`);
  }

  isFTEText(text: string) {
    return text.includes('FTE');
  }

  isHoursText(text: string) {
    return text.includes('hrs') || text.includes('Std');
  }

  getTextWithCurrency(text: string) {
    const currentCurrency = this.data.currentCurrency;

    var currencyText = this.currencySignsMapping[currentCurrency]
      ? this.currencySignsMapping[currentCurrency]
      : currentCurrency;

    if (currencyText == null) {
      return '';
    }

    return text.replace(this.currencyTag, currencyText);
  }

  private handleFoyResponse(data: FoyData) {
    if (data.currency != this.data.currentCurrency) {
      this.data.currentCurrency = data.currency;
      this.$currencyHasChanged.next(data.currency);
    }
    const survayHasChangedFromNormalIntoProcessIndustry = this.data.surveyType == null && data.surveyType == SurveyType.ProcessIndustry
    if (survayHasChangedFromNormalIntoProcessIndustry) {
      var dialogRef = this.dialog.open(InformationDialogComponent, {
            disableClose: false,
            data: {
              message: this.translate.instant('Survey has changed from normal into process industry')
            }
          });
    }
    if (data.surveyType != this.data.surveyType) {
      this.data.surveyType = data.surveyType;
      this.surveyTypeHasChanged.next(data.surveyType);
    }
  }
}
